import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ElementRef, Inject } from '@angular/core';

import { BehaviorSubject, map, Subscription, tap } from 'rxjs';
import * as am4core from '@amcharts/amcharts4/core';
import { clone } from 'underscore';

import { NeoFacetPrefixApiEnum } from '../../../../api/mentions/model/neo-facet-prefix-api.enum';
import { MentionSearchFacetTypesEnum } from '../../../../api/mentions/enum/mention-search-facet-types.enum';
import { SearchCriteriaQuerySentimentsEnum } from '../../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { DropdownTypesEnum } from '../../../../Common/Forms/enums/dropdown-types.enum';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { WordCloudChartModel } from '../../../../Common/Charts/models/WordCloudChart/word-cloud-chart.model';
import { WordCloudSeriesModel } from '../../../../Common/Charts/models/WordCloudChart/word-cloud-series.model';
import { SocialMetricAnalysisTypesEnum } from '../../../../Common/Charts/enums/social-analysis-types.enum';
import { DataPointModel } from '../../../../Common/Charts/models/data-point.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { MentionSearchModel } from '../../../../Monitor/Shared/models/mention-search.model';
import { MediaTypeModel } from '../../../../Monitor/Shared/models/media-type.model';
import { SentimentModel } from '../../../../Monitor/Shared/models/sentiment.model';
import { NetworkTypeEnum } from '../../../Core/enums/network-type.enum';
import { networkTypeToMediaTypeModelMap } from '@Social/Intelligence/models/network-type-to-media-type-model.map';
import { ReportSeriesFacetGeneratorEnum } from '../../../../Common/Reports/models/report-series-facet-generator.enum';
import { SearchCriteriaSortFieldEnum } from '../../../../api/mentions/enum/search-criteria-sort-field.enum';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { ChartMetadataModel } from '../../../../Common/Charts/models/chart-metadata.model';
import { SocialChartDetailsDialogService } from '@Social/Intelligence/services/social-chart-details-dialog.service';
import { TopTermsDataMappers } from './top-terms-data-mappers';
import { mentionsByEngagementNeoFacetRequest } from '@Social/Intelligence/models/neo-facet-requests';
import { ChartStylesModel } from '../../../../Common/Charts/models/chart-styles.model';
import { BlockModel } from '../../../../Common/PageBuilder/models/block.model';
import { topTermsListBlockType } from '@Social/Intelligence/models/top-terms-list-block-type.model';
import { TopTermsListChartDisplayElementModel } from '@Social/Intelligence/models/top-terms-list-chart-display-element.model';
import { TopTermEvent } from '../../../../Common/Charts/components/TopTermsChart/top-terms-chart.component';
import { AgprComponentStyleEnum } from '../../../../Common/Basic/enums/social-analysis-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { WordCloudChartOptionsModel } from '../../../../Common/Charts/models/WordCloudChart/word-cloud-chart-options.model';
import { ChartExportEventModel } from '../../../../Common/Charts/models/chart-export-event.model';
import { SocialChartExportModel, SocialChartExportService } from '@Social/Intelligence/services/social-chart-export.service';
import { userAppPreferencesDefault } from '../../../../Core/Users/data/user-app-preferences-default';
import { SortOrderEnum } from '../../../../api/shared/enums/sort-order.enum';
import { ReportSeriesFacetGeneratorOutput } from '@Common/Reports/models/report-series-facet-generator-output.model';
import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';
import { AbstractDashboardSearchService } from '@Monitor/Core/services/abstract-dashboard-search.service';

export type TopTermMetricEnum = MentionSearchFacetTypesEnum.words | MentionSearchFacetTypesEnum.handles | MentionSearchFacetTypesEnum.hashtags;

/**
 * Top Terms has three main configurations relating to its heading and the top terms chart heading.
 *      1. Single Mode - A single dropdown for 'Metric' in the heading
 *          1a. - Static subheading - The top terms chart heading does not reflect the chosen metric
 *          1b. - Dynamic subheading - The top terms chart heading reflects the chosen metric
 *      2. Multi Mode - Two dropdowns in the heading -- Currently unused
 */
export interface TopTermsTitleConfig {
    mainTitle: {
        mode: 'single' | 'multi';
        title: string;
    };
    subTitle: {
        mode: 'static' | 'dynamic';
        title: string;
    };
}

/**
 * Social wrapper component for the positive and negative sentiment top terms charts
 */
@Component({
    selector: 'agpr-top-terms',
    templateUrl: './top-terms.component.html',
    styleUrls: ['./top-terms.component.scss'],
    standalone: false
})
export class TopTermsComponent implements OnInit, OnDestroy {
    readonly dropdownTypes = DropdownTypesEnum;
    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly defaultChartColors = userAppPreferencesDefault.reportsPrefs.defaultChartColors;
    readonly mentionSearchFacetTypesEnum = MentionSearchFacetTypesEnum;

    @Input() id: string = '';
    @Input() series: ReportSeriesModel = null;
    @Input() accentColor: string = this.defaultChartColors.sentimentColors.positive;
    @Input() initialMetric: TopTermMetricEnum = MentionSearchFacetTypesEnum.hashtags;
    @Input() sentimentType: 'positive' | 'negative' = null;
    @Input() analysisType: SocialMetricAnalysisTypesEnum = SocialMetricAnalysisTypesEnum.volume;
    @Input() wordCloudSortField: SearchCriteriaSortFieldEnum = SearchCriteriaSortFieldEnum.relevancy;
    @Input() availableNetworkTypes: NetworkTypeEnum[] = [];
    @Input() config: TopTermsTitleConfig = {
        mainTitle: {
            mode: 'single',
            title: '',
        },
        subTitle: {
            mode: 'dynamic',
            title: ''
        }
    };
    @Input() set excludedNetworkTypes(networkTypes: NetworkTypeEnum[]) {
        this.showReddit = !networkTypes?.includes(NetworkTypeEnum.reddit);
    };
    @Input() useNeoFacets: boolean = true;
    @Input() forceSocialMediaTypes: boolean = false;
    @Input() hideWords: boolean = false;
    @Input() initialNetwork: NetworkTypeEnum = NetworkTypeEnum.all;

    @Output() topTermCardClicked = new EventEmitter<string>();
    @Output() wordCloudTermClicked = new EventEmitter<string>();

    options: { label: string; value: TopTermMetricEnum }[] = [
        {
            label: this.localization.transform(`Social.intelligence.charts.topTerms.${MentionSearchFacetTypesEnum.hashtags}`),
            value: MentionSearchFacetTypesEnum.hashtags
        },
        {
            label: this.localization.transform(`Social.intelligence.charts.topTerms.${MentionSearchFacetTypesEnum.handles}`),
            value: MentionSearchFacetTypesEnum.handles
        },
        {
            label: this.localization.transform(`Social.intelligence.charts.topTerms.${MentionSearchFacetTypesEnum.words}`),
            value: MentionSearchFacetTypesEnum.words
        },
    ];

    wordCloudChart: WordCloudChartModel;
    topTermsSeries: ReportSeriesModel[] = [];
    wordCloudSeries: WordCloudSeriesModel = null;
    dataPoints: DataPointModel[] = [];
    currentMetric: DropdownOptionModel<TopTermMetricEnum>;
    currentSocialType: NetworkTypeEnum = null;
    currentTermLabel: string = '';
    topTermsSubHeader: string = '';
    showReddit: boolean = true;
    loading: boolean = false;
    chartOptions: WordCloudChartOptionsModel = {};

    private subscriptions: Subscription[] = [];

    constructor(
        private localization: LocalizationService,
        @Inject('SocialSeriesService') private socialSeriesService: AbstractDashboardSearchService,
        private socialChartDetailsDialogService: SocialChartDetailsDialogService,
        public el: ElementRef,
        private chartExportService: SocialChartExportService
    ) { }

    ngOnInit(): void {
        this.currentTermLabel = this.dataPoints[0]?.label ?? '';

        if (!this.useNeoFacets) {
            this.options = this.options.filter(option => option.value !== MentionSearchFacetTypesEnum.handles);
        }

        if (this.hideWords) {
            this.options = this.options.filter(option => option.value !== MentionSearchFacetTypesEnum.words);
        }

        this.currentMetric = this.options.find(opt => opt.value === this.initialMetric);
        this.setTopTermsSubHeader();
        // An initial set of the word cloud series to ensure we at least have the series to show
        // something if no data present
        this.setWordCloudSeries();
        this.chartOptions.tooltipDescription1 = this.localization.transform(`Common.charts.topTerms.tooltipLabel.part1.${this.analysisType}`);
        this.chartOptions.tooltipDescription2 = this.localization.transform(`Common.charts.topTerms.tooltipLabel.part2.${this.analysisType}`);
        this.chartOptions.viewMentionString = this.localization.transform('Reports.chart.segmentCoverageAnalysisLabel');

        this.subscriptions.push(
            this.series.dataProvider.pipe(
                tap(data => {
                    this.loading = !data;
                }),
                map(data => {
                    return data ?? [];
                })
            ).subscribe(data => {
                this.dataPoints = data.slice(0, 5);
                this.currentTermLabel = this.dataPoints[0]?.label ?? '';
                // Word Cloud series data is dependent on Top Terms data
                if (data.length) {
                    this.setWordCloudSeries();
                } else {
                    this.deregisterWordCloudSeries();
                }
            }),
            this.wordCloudTermClicked.subscribe(term => {
                // Use the current selected term search
                const filterOverrides = this.series.mentions.filters.searchCriteria.clone();
                filterOverrides.query.keywordGroups[0].and.push(term);
                const url = this.wordCloudSeries.getSocialSeriesUrl(filterOverrides);
                this.socialChartDetailsDialogService.openDrillDownPage(url, this.wordCloudChart);
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions?.forEach(sub => sub.unsubscribe());
    }

    setSocialTypes(selectedType: NetworkTypeEnum) {
        this.currentSocialType = selectedType;
        this.socialSeriesService.deregisterSeries([this.series]);
        this.series.mentions.filters.searchCriteria.query.includedMediaTypes = this.getMediaTypesFromSelectedType();
        this.socialSeriesService.fulfillSeries([this.series]);
    }

    onMetricChange() {
        this.socialSeriesService.deregisterSeries([this.series]);
        this.updateSeriesOnMetricChange(this.series);
        this.socialSeriesService.fulfillSeries([this.series]);
        this.setTopTermsSubHeader();
    }

    updateSeriesOnMetricChange(series: ReportSeriesModel): void {
        if (this.currentMetric.value === MentionSearchFacetTypesEnum.words) {
            // We use the word cloud api for Top "Words"
            series.mentions.facetGenerator = null;
            series.mentions.mentionRequestGenerator = () => {
                return {
                    sortOrder: SortOrderEnum.descending,
                    sortField: this.wordCloudSortField,
                    pageSize: 100,
                    isWordCloudRequest: true
                };
            };
            series.mentions.dataMapper = TopTermsDataMappers.wordsDataMapper(series);
        } else {
            series.mentions.mentionRequestGenerator = null;
            // Emit null for loading
            series.dataProvider.next(null);
            const isHashtagSearch = this.currentMetric.value === MentionSearchFacetTypesEnum.hashtags;

            series.mentions.facetGenerator = this.getSeriesFacetGenerator(isHashtagSearch);

            if (this.sentimentType) {
                series.mentions.filters.searchCriteria.query.sentiments = [ new SentimentModel(this.getSearchCriteriaQuerySentimentsEnum()) ];
            }

            if (this.useNeoFacets) {
                series.mentions.dataMapper = TopTermsDataMappers.neofacetEngagementDataMapper(series, isHashtagSearch);
            } else {
                series.mentions.dataMapper = TopTermsDataMappers.hashtagDataMapper(series);
            }
        }

        series.termType = this.currentMetric.value;
    }

    onTooltipClicked(event: { url: string; label: string }) {
        this.topTermCardClicked.emit(event.url);

        const chartStyles = new ChartStylesModel();
        chartStyles.colorMap['accentColor'] = this.accentColor;

        const topTermsBlock = new BlockModel(
            null,
            topTermsListBlockType,
            new TopTermsListChartDisplayElementModel(
                this.localization.transform(this.topTermsSubHeader),
                [this.series.clone()],
                event.label,
                chartStyles,
                this.analysisType
            )
        );

        this.socialChartDetailsDialogService.openDrillDownPage(event.url, topTermsBlock);
    }

    selectTerm(selectedTerm: TopTermEvent) {
        if (this.series) {
            this.deregisterWordCloudSeries();
            // Remove old term first
            const keywords = this.series.mentions.filters.searchCriteria.query.keywordGroups[0];
            keywords.and = keywords.and.filter(keyword => {
                return keyword !== this.currentTermLabel;
            });
        }
        this.currentTermLabel = selectedTerm.label;
        this.setWordCloudSeries();
    }

    private deregisterWordCloudSeries() {
        if (this.wordCloudSeries) {
            // Ensure any subscribers reset their data before the deregister
            this.wordCloudSeries.dataProvider.next([]);
            this.socialSeriesService.deregisterSeries([this.wordCloudSeries]);

            if (this.wordCloudChart?.metadata) {
                // Reset the object reference to ensure updated chart/label
                this.wordCloudChart = this.wordCloudChart.clone();
                this.wordCloudChart.metadata.noContentLabel = this.getNoContentLabel('noContent');
            }
        }
        this.wordCloudSeries = null;
    }

    private setTopTermsSubHeader() {
        if (!this.config) {
            return;
        }

        if (this.config.subTitle.mode === 'static') {
            this.topTermsSubHeader = this.localization.transform(this.config.subTitle.title);
            return;
        }

        this.topTermsSubHeader = `${this.localization.transform(this.config.subTitle.title)} ${this.localization.transform(this.currentMetric.label)}`;
    }

    private setWordCloudSeries() {
        this.deregisterWordCloudSeries();

        if (!this.currentTermLabel || (this.currentTermLabel === '')) {
            return;
        }

        const wordCloudSeries = new WordCloudSeriesModel();
        // Word Cloud specific data
        wordCloudSeries.sentimentType = this.sentimentType;
        wordCloudSeries.accentColor = this.accentColor;
        wordCloudSeries.eventHandlers = {
            wordClicked: (word: string) => {
                this.wordCloudTermClicked.emit(word);
                return false;
            },
            viewPostsClicked: (word: string) => {
                this.wordCloudTermClicked.emit(word);
                return false;
            }
        };
        // Social Series specific data
        wordCloudSeries.mentionProvider = new BehaviorSubject<Mention[]>(null);
        wordCloudSeries.mentions.mentionRequestGenerator = () => {
            return {
                sortOrder: SortOrderEnum.descending,
                sortField: this.wordCloudSortField,
                pageSize: 100,
                isWordCloudRequest: true
            };
        };

        wordCloudSeries.mentions.dataMapper = (response) => {
            if (response.wordCloudResults[0]?.scores.length === 0) {
                if (this.wordCloudChart?.metadata) {
                    const part1 = this.localization.transform('Social.intelligence.charts.wordCloud.noContentGenerated.part1');
                    const part2 = this.localization.transform('Social.intelligence.charts.wordCloud.noContentGenerated.part2');
                    this.wordCloudChart.metadata.noContentLabel =
                    `<div>
                        <div style="font-style: italic; font-size: 0.875rem;">${part1}</div>
                        <div style="font-style: italic; margin-top:5px;">${part2}</div>
                    </div>`;
                }
            }

            const values = response.wordCloudResults[0]?.scores.slice(0, 10) ?? [];
            return values.map((val, i) => {
                const filterOverrides = this.series.mentions.filters.searchCriteria.clone();
                filterOverrides.query.keywordGroups[0].and.push(val.term);
                return {
                    tag: val.term,
                    count: val.score,
                    'viewMentionUrl': this.wordCloudSeries.getSocialSeriesUrl(filterOverrides),
                } as DataPointModel;
            });
        };
        wordCloudSeries.mentions.filters = new MentionSearchModel();

        // Clone so as not to pollute original series
        const keywords = this.series.mentions.filters.searchCriteria.query.clone().keywordGroups;
        keywords[0].and.push(this.currentTermLabel);
        wordCloudSeries.mentions.filters.searchCriteria.query.keywordGroups = keywords;

        if (this.sentimentType) {
            wordCloudSeries.mentions.filters.searchCriteria.query.sentiments = [ new SentimentModel(this.getSearchCriteriaQuerySentimentsEnum()) ];
        }

        if (this.forceSocialMediaTypes) {
            wordCloudSeries.mentions.filters.searchCriteria.query.includedMediaTypes = MediaTypeModel.getSocialMediaTypes();
        }

        this.wordCloudSeries = wordCloudSeries;

        this.socialSeriesService.fulfillSeries([this.wordCloudSeries]);

        const styles = new ChartStylesModel();
        styles.chartHeight = 400;
        styles.colorMap.barColor = this.sentimentType === 'negative' ?
            this.defaultChartColors.sentimentColors.negative : this.defaultChartColors.sentimentColors.positive;

        const noContentText = this.localization.transform('Social.intelligence.wordCloudChart.noContent');
        this.wordCloudChart = new WordCloudChartModel(
            [this.wordCloudSeries],
            {
                styles: styles,
                isSocialStyles: true,
                numOfItems: 10,
                viewMentionString: this.localization.transform('Reports.chart.segmentCoverageAnalysisLabel')
            },
            {
                noContentLabel: `<span>${noContentText}</span>`
            } as ChartMetadataModel
        );
    }

    private getSeriesFacetGenerator(isHashtagSearch: boolean): () => ReportSeriesFacetGeneratorOutput {
        if (this.useNeoFacets) {
            const neofacetRequest = clone(mentionsByEngagementNeoFacetRequest);
            neofacetRequest.prefix = isHashtagSearch
                ? NeoFacetPrefixApiEnum.hashtag
                : NeoFacetPrefixApiEnum.handle;

            return () => {
                return {
                    facet: neofacetRequest,
                    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                };
            };
        } else {
            return () => {
                return {
                    facet: [ this.currentMetric.value ],
                    identifier: ReportSeriesFacetGeneratorEnum.facet
                };
            };
        }
    }

    private getSearchCriteriaQuerySentimentsEnum(): SearchCriteriaQuerySentimentsEnum {
        if (this.sentimentType === 'positive') {
            return SearchCriteriaQuerySentimentsEnum.positive;
        } else  {
            return SearchCriteriaQuerySentimentsEnum.negative;
        }
    }


    public getMediaTypesFromSelectedType(networkType?: NetworkTypeEnum): MediaTypeModel[] {
        networkType = networkType || this.currentSocialType;
        // Since we're filtering on agents, passing empty array for all will match other series calls (1 less api request)
        return (networkType === NetworkTypeEnum.all) ?
            (this.forceSocialMediaTypes ? MediaTypeModel.getSocialMediaTypes() : []) : networkTypeToMediaTypeModelMap.get(this.currentSocialType);
    }

    private getNoContentLabel(choice: 'noContent' | 'noContentGenerated'): string {
        const result = choice === 'noContent'
            ? this.localization.transform('Social.intelligence.wordCloudChart.noContent')
            : this.localization.transform('Social.intelligence.charts.wordCloud.noContentGenerated');

        return `<span style="font-style: italic;">${result}</span>`;
    }

    // @HACK: have to pass chartElementRef from html template or else it would not export the image properly for some charts
    public exportChart(event: ChartExportEventModel, chartElementRef: any) {
        let exportTitle = this.config?.mainTitle.mode === 'single' ?
            this.localization.transform(this.config.mainTitle.title)
            : this.localization.transform('Social.intelligence.charts.topTerms.multi.heading1');
        exportTitle += ` ${this.currentMetric.label}`;
        if (this.config?.mainTitle.mode === 'multi') {
            // eslint-disable-next-line max-len
            exportTitle += `${this.localization.transform('Social.intelligence.charts.topTerms.multi.heading2')} ${this.localization.transform('Social.intelligence.themeAnalytics.engagement')}`;
        }

        const exportDataArray = [];
        this.dataPoints.forEach(data => {
            const obj = {};
            obj['termName'] = data.label;
            obj['termValue'] = data.count;
            exportDataArray.push(obj);
        });
        const exportModel: SocialChartExportModel = {
            title: exportTitle,
            exportData: exportDataArray,
            exportLabelDefinitions: new Map<string, string>([
                ['termName', this.localization.transform('Social.export.label.termName')],
                ['termValue', this.localization.transform('Social.export.label.termValue')]
            ])
        };

        const exportType = event.eventKey.toLowerCase() as keyof am4core.IExportOptions;
        if (exportType === 'csv' || exportType === 'json') {
            this.chartExportService.exportData(exportType, exportModel);
        } else {
            const exportMenuElement = chartElementRef.querySelector('.agpr-top-terms-exportMenu');
            const expandPostIconElement = chartElementRef.querySelectorAll('.fa-solid fa-angle-down');
            const dropdownIconElement = chartElementRef.querySelector('.agpr-dropdown-triggerIcon');
            exportMenuElement.style.display = 'none';
            expandPostIconElement.forEach(element => {
                element.style.display = 'none';
            });
            dropdownIconElement.style.display = 'none';
            this.chartExportService.exportImage(exportType, chartElementRef, exportModel.title).then(() => {
                exportMenuElement.style.display = 'block';
                expandPostIconElement.forEach(element => {
                    element.style.display = 'block';
                });
                dropdownIconElement.style.display = 'unset';
            });
        }
    }
}
